import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  downloadBookingInvoice,
  fetchBookingInvoicePdfBlob,
  fetchDraftInvoicePdfBlob,
} from '../../services/api';
import { getBookingDrawerTheme } from '../../utils/bookingDrawerLayout';
import { downloadBlob, usesShareSheetForDownload } from '../../utils/downloadBlob';
import PdfFitPreview from '../common/PdfFitPreview';
import BookingDraftInvoiceEditor from './BookingDraftInvoiceEditor';

function FancyLoadingState({ isDark, accentColor, drawerTheme, message = 'Preparing invoice preview', compact = false }) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: compact ? 280 : 420, gap: compact ? 1.5 : 2, px: compact ? 2 : 3 }}>
      <Box
        sx={{
          width: 78,
          height: 78,
          borderRadius: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#06251f',
          background: `linear-gradient(180deg, ${alpha(accentColor, 0.98)} 0%, ${alpha(accentColor, 0.78)} 100%)`,
          boxShadow: `0 18px 34px ${alpha(accentColor, 0.28)}`,
          animation: 'invoicePreviewPulse 1.8s ease-in-out infinite',
          '@keyframes invoicePreviewPulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.04)' },
          },
        }}
      >
        <PictureAsPdfIcon sx={{ fontSize: 36 }} />
      </Box>
      <Box sx={{ textAlign: 'center', maxWidth: 320 }}>
        <Typography sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a', mb: 0.5 }}>
          {message}
        </Typography>
        <Typography variant="body2" sx={{ color: drawerTheme.subtleText, lineHeight: 1.55 }}>
          RoomNexa is building your tax invoice with booking details and payment summary.
        </Typography>
      </Box>
      <Box
        sx={{
          width: 'min(260px, 72%)',
          height: 6,
          borderRadius: 999,
          overflow: 'hidden',
          backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08),
        }}
      >
        <Box
          sx={{
            width: '42%',
            height: '100%',
            borderRadius: 999,
            background: `linear-gradient(90deg, ${alpha(accentColor, 0.2)} 0%, ${accentColor} 100%)`,
            animation: 'invoicePreviewProgress 1.2s ease-in-out infinite alternate',
            '@keyframes invoicePreviewProgress': {
              '0%': { transform: 'translateX(-35%)' },
              '100%': { transform: 'translateX(175%)' },
            },
          }}
        />
      </Box>
    </Stack>
  );
}

function PdfPreviewFrame({
  pdfUrl,
  isUpdating = false,
  compact = false,
  accentColor = '#4ce1b6',
  onReady,
}) {
  const minHeight = compact ? 280 : 360;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(76,225,182,0.16)',
        boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
        bgcolor: '#e8edf3',
        width: '100%',
        minHeight,
      }}
    >
      <PdfFitPreview
        pdfUrl={pdfUrl}
        accentColor={accentColor}
        minHeight={minHeight}
        onReady={onReady}
      />
      {isUpdating ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            bgcolor: 'rgba(255,255,255,0.45)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            p: 1,
            zIndex: 2,
          }}
        >
          <Chip
            size="small"
            label="Updating…"
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: '0.68rem',
              bgcolor: 'rgba(76,225,182,0.16)',
              color: '#0f766e',
              border: '1px solid rgba(76,225,182,0.35)',
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function DraftPreviewPanel({
  pdfUrl,
  generatingDraft,
  error,
  isDark,
  accentColor,
  drawerTheme,
  compact = false,
}) {
  const [frameReady, setFrameReady] = useState(false);
  const showInitialLoading = !pdfUrl && !error;

  useEffect(() => {
    setFrameReady(false);
  }, [pdfUrl]);

  if (error) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: compact ? 280 : 360, p: 2 }}>
        <Alert severity="error" sx={{ width: '100%', borderRadius: 2.5 }}>
          {error}
        </Alert>
      </Stack>
    );
  }

  if (showInitialLoading) {
    return (
      <FancyLoadingState
        isDark={isDark}
        accentColor={accentColor}
        drawerTheme={drawerTheme}
        message={generatingDraft ? 'Loading draft invoice' : 'Preparing draft preview'}
        compact={compact}
      />
    );
  }

  return (
    <PdfPreviewFrame
      pdfUrl={pdfUrl}
      isUpdating={generatingDraft && frameReady}
      compact={compact}
      accentColor={accentColor}
      onReady={() => setFrameReady(true)}
    />
  );
}

export default function BookingInvoicePreviewDialog({
  open,
  onClose,
  bookingId,
  invoiceNumber,
  bookingCode,
  guestName,
  isDark,
  accentColor = '#4ce1b6',
  invoiceScope = 'hotel',
  fetchOfficialPdfBlob = fetchBookingInvoicePdfBlob,
  downloadOfficialInvoice = downloadBookingInvoice,
  fetchDraftPdfBlob = fetchDraftInvoicePdfBlob,
  fetchDraftDefaults,
}) {
  const drawerTheme = getBookingDrawerTheme(isDark);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const shareSheetDownload = usesShareSheetForDownload();
  const [mode, setMode] = useState('official');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [officialPdfUrl, setOfficialPdfUrl] = useState(null);
  const [draftPdfUrl, setDraftPdfUrl] = useState(null);
  const [draftGenerated, setDraftGenerated] = useState(false);
  const [officialFrameReady, setOfficialFrameReady] = useState(false);
  const officialPdfUrlRef = useRef(null);
  const draftPdfUrlRef = useRef(null);
  const cachedOfficialKeyRef = useRef('');
  const draftRequestIdRef = useRef(0);
  const lastDraftPayloadRef = useRef('');
  const inFlightDraftPayloadRef = useRef('');
  const loadRequestIdRef = useRef(0);

  const officialCacheKey = `${bookingId || ''}:${invoiceScope || 'hotel'}`;

  const revokeOfficialPdf = useCallback(() => {
    if (officialPdfUrlRef.current) {
      window.URL.revokeObjectURL(officialPdfUrlRef.current);
      officialPdfUrlRef.current = null;
    }
    setOfficialPdfUrl(null);
    setOfficialFrameReady(false);
    cachedOfficialKeyRef.current = '';
  }, []);

  const revokeDraftPdf = useCallback(() => {
    if (draftPdfUrlRef.current) {
      window.URL.revokeObjectURL(draftPdfUrlRef.current);
      draftPdfUrlRef.current = null;
    }
    setDraftPdfUrl(null);
    setDraftGenerated(false);
    lastDraftPayloadRef.current = '';
    inFlightDraftPayloadRef.current = '';
  }, []);

  const revokeAllPdfs = useCallback(() => {
    revokeOfficialPdf();
    revokeDraftPdf();
  }, [revokeDraftPdf, revokeOfficialPdf]);

  const loadOfficialPdf = useCallback(async () => {
    if (!bookingId) {
      setError('Booking not found for this invoice.');
      setLoading(false);
      return;
    }
    // Keep the last PDF for this booking/scope so reopening the dialog is instant.
    if (officialPdfUrlRef.current && cachedOfficialKeyRef.current === officialCacheKey) {
      setOfficialPdfUrl(officialPdfUrlRef.current);
      setLoading(false);
      return;
    }

    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setError(null);
    setOfficialFrameReady(false);
    try {
      const blob = await fetchOfficialPdfBlob(bookingId, invoiceScope);
      if (requestId !== loadRequestIdRef.current) return;
      const previousUrl = officialPdfUrlRef.current;
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      officialPdfUrlRef.current = url;
      cachedOfficialKeyRef.current = officialCacheKey;
      setOfficialPdfUrl(url);
      if (previousUrl && previousUrl !== url) {
        window.URL.revokeObjectURL(previousUrl);
      }
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to load invoice PDF.');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [bookingId, invoiceScope, fetchOfficialPdfBlob, officialCacheKey]);

  // Drop cached PDFs when the booking/scope changes.
  useEffect(() => {
    revokeAllPdfs();
  }, [bookingId, invoiceScope, revokeAllPdfs]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setDownloading(false);
      setGeneratingDraft(false);
      setMode('official');
      // Keep official PDF cached while the dialog is closed (same booking).
      if (!officialPdfUrlRef.current) {
        setLoading(false);
      }
      return undefined;
    }

    if (mode === 'official') {
      // Always wait for the preview surface to paint again after open/remount.
      setOfficialFrameReady(false);
      // Show the loader on the first paint — avoids an empty panel flash.
      if (!officialPdfUrlRef.current || cachedOfficialKeyRef.current !== officialCacheKey) {
        setLoading(true);
      }
      loadOfficialPdf();
      return undefined;
    }

    setError(null);
    setLoading(false);
    return undefined;
  }, [open, mode, loadOfficialPdf, officialCacheKey]);

  useEffect(() => () => revokeAllPdfs(), [revokeAllPdfs]);

  useEffect(() => {
    setOfficialFrameReady(false);
  }, [officialPdfUrl]);

  const handleGenerateDraft = useCallback(async (payload, options = {}) => {
    if (!bookingId) return;

    const payloadKey = JSON.stringify(payload);
    if (
      !options.force
      && (payloadKey === lastDraftPayloadRef.current || payloadKey === inFlightDraftPayloadRef.current)
    ) {
      return;
    }

    const requestId = ++draftRequestIdRef.current;
    inFlightDraftPayloadRef.current = payloadKey;
    setGeneratingDraft(true);
    setError(null);

    try {
      const blob = await fetchDraftPdfBlob(bookingId, payload);
      if (requestId !== draftRequestIdRef.current) return;

      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const previousUrl = draftPdfUrlRef.current;
      draftPdfUrlRef.current = url;
      setDraftPdfUrl(url);
      setDraftGenerated(true);
      lastDraftPayloadRef.current = payloadKey;
      if (previousUrl && previousUrl !== url) {
        window.URL.revokeObjectURL(previousUrl);
      }
    } catch (err) {
      if (requestId !== draftRequestIdRef.current) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to generate draft invoice.');
    } finally {
      if (requestId === draftRequestIdRef.current) {
        inFlightDraftPayloadRef.current = '';
        setGeneratingDraft(false);
      }
    }
  }, [bookingId, fetchDraftPdfBlob]);

  const clearDraftCache = useCallback(() => {
    lastDraftPayloadRef.current = '';
    inFlightDraftPayloadRef.current = '';
  }, []);

  const handleDownloadOfficial = async () => {
    if (!bookingId || downloading) return;
    setDownloading(true);
    try {
      await downloadOfficialInvoice(bookingId, invoiceScope);
    } catch (err) {
      setError(err?.message || 'Failed to download invoice PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDraft = async () => {
    if (!draftPdfUrl || downloading) return;
    setDownloading(true);
    try {
      const code = bookingCode || `booking-${bookingId}`;
      const filename = `DraftInvoice_${code}.pdf`;
      const response = await fetch(draftPdfUrl);
      const blob = await response.blob();
      await downloadBlob(blob, filename);
    } catch (err) {
      setError(err?.message || 'Failed to download draft invoice PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const footerButtonSx = {
    minWidth: { xs: 0, sm: 118 },
    width: { xs: '100%', sm: 'auto' },
    px: 2,
    py: 1,
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 800,
    letterSpacing: 0.2,
    color: isDark ? '#f4f7fb' : '#172033',
    borderColor: isDark ? alpha('#ffffff', 0.14) : alpha('#101828', 0.16),
    bgcolor: isDark ? alpha('#ffffff', 0.045) : '#ffffff',
    boxShadow: isDark ? '0 10px 24px rgba(8, 12, 24, 0.22)' : '0 10px 24px rgba(16, 24, 40, 0.08)',
    '& .MuiButton-startIcon': { mr: 0.75 },
    '&:hover': {
      borderColor: alpha(accentColor, 0.7),
      bgcolor: isDark ? alpha(accentColor, 0.12) : alpha(accentColor, 0.08),
    },
    '&.Mui-disabled': {
      color: isDark ? alpha('#ffffff', 0.36) : alpha('#101828', 0.36),
      borderColor: isDark ? alpha('#ffffff', 0.08) : alpha('#101828', 0.08),
    },
  };

  const primaryActionSx = {
    minWidth: { xs: 0, sm: 132 },
    width: { xs: '100%', sm: 'auto' },
    px: 2.2,
    py: 1,
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 800,
    letterSpacing: 0.2,
    color: '#02221b',
    background: 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)',
    border: '1px solid rgba(88, 242, 206, 0.85)',
    boxShadow: '0 10px 26px rgba(36, 226, 178, 0.28)',
    '& .MuiButton-startIcon': { mr: 0.75, color: '#02221b' },
    '&:hover': {
      background: 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)',
    },
    '&.Mui-disabled': {
      color: alpha('#02221b', 0.45),
      background: alpha(accentColor, 0.25),
      borderColor: alpha(accentColor, 0.2),
      boxShadow: 'none',
    },
  };

  const isDraftMode = mode === 'draft';
  const activePdfUrl = isDraftMode ? draftPdfUrl : officialPdfUrl;
  const officialBlobReady = !loading && !error && Boolean(officialPdfUrl);
  const previewReady = isDraftMode
    ? Boolean(draftPdfUrl) && !error
    : officialBlobReady && officialFrameReady;
  // Keep the fancy loader until the first page has painted (no white iframe flash).
  const showOfficialLoading = !error && (loading || !officialPdfUrl || (Boolean(officialPdfUrl) && !officialFrameReady));
  const canDownload = isDraftMode ? draftGenerated && Boolean(draftPdfUrl) : officialBlobReady;

  const handlePrint = () => {
    if (!activePdfUrl) return;
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    printFrame.src = activePdfUrl;
    document.body.appendChild(printFrame);
    printFrame.onload = () => {
      try {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
      } finally {
        window.setTimeout(() => printFrame.remove(), 2000);
      }
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth={isDraftMode ? 'lg' : 'md'}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: isMobile ? 0 : '28px', md: '28px' },
          overflow: 'hidden',
          position: 'relative',
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 32px)' },
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid ${alpha(accentColor, isDark ? 0.42 : 0.34)}`,
          background: isDark
            ? 'linear-gradient(145deg, rgba(30, 35, 48, 0.98) 0%, rgba(18, 23, 35, 0.99) 100%)'
            : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(241, 245, 249, 0.99) 100%)',
          boxShadow: isDark
            ? `0 28px 70px rgba(0, 0, 0, 0.46), 0 0 42px ${alpha(accentColor, 0.22)}`
            : `0 28px 70px rgba(15, 23, 42, 0.22), 0 0 42px ${alpha(accentColor, 0.28)}`,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          borderBottom: `1px solid ${alpha(accentColor, isDark ? 0.22 : 0.16)}`,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(accentColor, 0.14)} 0%, rgba(15, 23, 42, 0.92) 52%, ${alpha(accentColor, 0.08)} 100%)`
            : `linear-gradient(135deg, ${alpha(accentColor, 0.18)} 0%, rgba(255,255,255,0.98) 52%, ${alpha(accentColor, 0.1)} 100%)`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                color: '#06251f',
                background: `linear-gradient(180deg, ${alpha(accentColor, 0.98)} 0%, ${alpha(accentColor, 0.78)} 100%)`,
                boxShadow: `0 8px 18px ${alpha(accentColor, 0.22)}`,
                border: `1px solid ${alpha(accentColor, 0.55)}`,
              }}
            >
              {isDraftMode ? <EditNoteIcon sx={{ fontSize: 20 }} /> : <ReceiptLongIcon sx={{ fontSize: 20 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Typography
                  variant="caption"
                  sx={{
                    color: accentColor,
                    fontWeight: 800,
                    letterSpacing: 1.1,
                    lineHeight: 1,
                    textTransform: 'uppercase',
                    fontSize: '0.68rem',
                  }}
                >
                  {isDraftMode ? 'Draft invoice' : 'Invoice preview'}
                </Typography>
                <Chip
                  size="small"
                  icon={<PictureAsPdfIcon sx={{ fontSize: '12px !important' }} />}
                  label={isDraftMode ? 'Draft PDF' : 'PDF'}
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    fontSize: '0.62rem',
                    bgcolor: alpha(accentColor, isDark ? 0.16 : 0.12),
                    color: accentColor,
                    border: `1px solid ${alpha(accentColor, 0.35)}`,
                    '& .MuiChip-icon': { color: accentColor, ml: '6px' },
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Stack>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: isDark ? drawerTheme.text : '#0f172a',
                  lineHeight: 1.2,
                  mt: 0.25,
                  letterSpacing: -0.1,
                  fontSize: '0.95rem',
                }}
              >
                {isDraftMode ? 'Customize & send without saving' : invoiceNumber || 'Tax invoice'}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: drawerTheme.subtleText,
              border: `1px solid ${alpha(accentColor, 0.22)}`,
              bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha('#ffffff', 0.72),
              '&:hover': {
                bgcolor: isDark ? alpha(accentColor, 0.12) : alpha(accentColor, 0.08),
                color: accentColor,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Tabs
          value={mode}
          onChange={(_, value) => setMode(value)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            mt: 1,
            minHeight: 32,
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0.5,
              px: { xs: 1, sm: 1.25 },
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.72rem', sm: '0.78rem' },
              color: drawerTheme.subtleText,
            },
            '& .Mui-selected': { color: `${accentColor} !important` },
            '& .MuiTabs-indicator': { backgroundColor: accentColor, height: 2, borderRadius: 999 },
          }}
        >
          <Tab value="official" label={isMobile ? 'Official' : 'Official invoice'} />
          <Tab
            value="draft"
            label={isMobile ? 'Draft' : 'Draft invoice'}
            icon={isMobile ? undefined : <EditNoteIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: { xs: 1.25, sm: 2.75 },
          pt: { xs: 1, sm: 2 },
          background: isDark
            ? `radial-gradient(circle at top, ${alpha(accentColor, 0.06)} 0%, transparent 42%)`
            : `radial-gradient(circle at top, ${alpha(accentColor, 0.08)} 0%, transparent 42%)`,
        }}
      >
        {isDraftMode ? (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: '14px', sm: '18px' },
                maxHeight: { lg: '62vh' },
                overflow: 'auto',
                border: `1px solid ${alpha(accentColor, isDark ? 0.22 : 0.18)}`,
                bgcolor: isDark ? alpha('#020617', 0.35) : alpha('#ffffff', 0.92),
              }}
            >
              <BookingDraftInvoiceEditor
                bookingId={bookingId}
                invoiceScope={invoiceScope}
                isDark={isDark}
                accentColor={accentColor}
                isMobile={isMobile}
                fetchDefaults={fetchDraftDefaults}
                onGenerateDraft={handleGenerateDraft}
                onClearDraftCache={clearDraftCache}
                generating={generatingDraft}
              />
            </Paper>
            <Paper
              elevation={0}
              sx={{
                flex: 1.2,
                minHeight: { xs: 300, sm: 360, lg: 420 },
                borderRadius: { xs: '14px', sm: '20px' },
                overflow: 'hidden',
                border: `1px solid ${alpha(accentColor, isDark ? 0.28 : 0.2)}`,
                bgcolor: isDark ? alpha('#020617', 0.35) : alpha('#f8fafc', 0.9),
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: `1px solid ${alpha(accentColor, isDark ? 0.18 : 0.14)}`,
                  bgcolor: isDark ? alpha('#020617', 0.45) : alpha('#ffffff', 0.72),
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: accentColor }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? drawerTheme.text : '#334155' }}>
                    Draft preview
                  </Typography>
                </Stack>
                {draftGenerated && draftPdfUrl && !generatingDraft ? (
                  <Chip
                    size="small"
                    label="Draft ready"
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      bgcolor: alpha('#ed6c02', 0.16),
                      color: '#ed6c02',
                      border: '1px solid rgba(237,108,2,0.35)',
                    }}
                  />
                ) : generatingDraft && !draftPdfUrl ? (
                  <Chip
                    size="small"
                    label="Loading…"
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      bgcolor: alpha(accentColor, 0.16),
                      color: accentColor,
                      border: `1px solid ${alpha(accentColor, 0.35)}`,
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ p: { xs: 1, sm: 1.25 } }}>
                <DraftPreviewPanel
                  pdfUrl={draftPdfUrl}
                  generatingDraft={generatingDraft}
                  error={error}
                  isDark={isDark}
                  accentColor={accentColor}
                  drawerTheme={drawerTheme}
                  compact={isMobile}
                />
              </Box>
            </Paper>
          </Stack>
        ) : (
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              minHeight: { xs: 300, sm: 420 },
              borderRadius: { xs: '14px', sm: '20px' },
              overflow: 'hidden',
              border: `1px solid ${alpha(accentColor, isDark ? 0.28 : 0.2)}`,
              bgcolor: isDark ? alpha('#020617', 0.35) : alpha('#f8fafc', 0.9),
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${alpha(accentColor, isDark ? 0.18 : 0.14)}`,
                bgcolor: isDark ? alpha('#020617', 0.45) : alpha('#ffffff', 0.72),
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: accentColor }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? drawerTheme.text : '#334155' }}>
                  Document preview
                </Typography>
              </Stack>
              {previewReady ? (
                <Chip
                  size="small"
                  label="Ready"
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    bgcolor: alpha(accentColor, 0.16),
                    color: accentColor,
                    border: `1px solid ${alpha(accentColor, 0.35)}`,
                  }}
                />
              ) : !error ? (
                <Chip
                  size="small"
                  label={loading || !officialPdfUrl ? 'Loading…' : 'Rendering…'}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    bgcolor: alpha(accentColor, 0.16),
                    color: accentColor,
                    border: `1px solid ${alpha(accentColor, 0.35)}`,
                  }}
                />
              ) : null}
            </Box>
            <Box sx={{ p: { xs: 1, sm: 1.25 }, bgcolor: '#e8edf3', minHeight: { xs: 280, sm: 420 } }}>
              {error ? (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: { xs: 280, sm: 420 }, p: 3 }}>
                  <Alert severity="error" sx={{ width: '100%', borderRadius: 2.5 }}>
                    {error}
                  </Alert>
                </Stack>
              ) : (
                <Box sx={{ position: 'relative', minHeight: { xs: 280, sm: 420 } }}>
                  {officialPdfUrl ? (
                    <PdfPreviewFrame
                      pdfUrl={officialPdfUrl}
                      compact={isMobile}
                      accentColor={accentColor}
                      onReady={() => setOfficialFrameReady(true)}
                    />
                  ) : null}
                  {showOfficialLoading ? (
                    <Box
                      sx={{
                        position: officialPdfUrl ? 'absolute' : 'relative',
                        inset: officialPdfUrl ? 0 : 'auto',
                        zIndex: 3,
                        bgcolor: '#e8edf3',
                      }}
                    >
                      <FancyLoadingState
                        isDark={isDark}
                        accentColor={accentColor}
                        drawerTheme={drawerTheme}
                        compact={isMobile}
                        message={loading || !officialPdfUrl ? 'Preparing invoice preview' : 'Rendering invoice preview'}
                      />
                    </Box>
                  ) : null}
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.25, sm: 2.75 },
          py: { xs: 1.5, sm: 2 },
          borderTop: `1px solid ${alpha(accentColor, isDark ? 0.2 : 0.14)}`,
          background: isDark
            ? `linear-gradient(180deg, rgba(18, 23, 35, 0.96) 0%, rgba(10, 14, 24, 0.98) 100%)`
            : `linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(241, 245, 249, 0.98) 100%)`,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.25}
        >
          <Typography
            variant="caption"
            sx={{
              color: drawerTheme.subtleText,
              fontWeight: 600,
              px: 0.25,
              display: { xs: isDraftMode ? 'none' : 'block', sm: 'block' },
            }}
          >
            {isDraftMode
              ? 'Draft invoices are for customer sharing only — booking and payment data stay unchanged.'
              : 'Preview, print, or download this invoice for your records.'}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="flex-end"
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 'auto' } }}
          >
            <Button variant="outlined" onClick={onClose} sx={footerButtonSx}>
              Close
            </Button>
            <Button
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={isDraftMode ? handleDownloadDraft : handleDownloadOfficial}
              disabled={loading || generatingDraft || !!error || !canDownload || downloading}
              sx={footerButtonSx}
            >
              {downloading
                ? (shareSheetDownload ? 'Preparing…' : 'Downloading…')
                : shareSheetDownload
                  ? (isDraftMode ? 'Save / share draft' : 'Save / share PDF')
                  : (isDraftMode ? 'Download draft' : 'Download PDF')}
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={loading || generatingDraft || !!error || !canDownload}
              sx={primaryActionSx}
            >
              Print
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
